import type { CSSProperties, ReactNode } from 'react'
import { PANEL } from '../../../shared/components'
import { GAME_ART_BY_ID, GAME_FOR_SKILL, MOTOR_TINT } from '../../../shared/ui/gameArt'
import { isContentSkill, type Skill } from '../../../shared/types/prisma-enums'
import type { BarSeries, Ratio, SkillShare, WeekdayRow } from './activityModel'

/**
 * C2's charts, drawn as inline SVG and plain boxes.
 *
 * 🔴 NO CHART LIBRARY, AND THAT IS A DELIBERATE REFUSAL RATHER THAN A SHORTCUT.
 * `package.json` records why the router and the query library were kept out of
 * Unit 0 — a dependency arrives with its own idea of colour, typography and
 * motion, and every one of those is fixed here by `design-tokens.css`. These
 * charts are four shapes: a row of bars, a row of labelled bars, a ring, and a
 * list. A library would be ~40 KB on the one tier that is read on a phone over a
 * mobile bundle, to draw rectangles.
 *
 * 🔴 IDENTITY IS NEVER COLOUR ALONE ON THIS SCREEN, AND THAT IS NOT A STYLE
 * CHOICE — IT IS WHAT THE PALETTE ALLOWS. §3 fixes one colour per skill and
 * those eight tokens are not a categorical chart palette: run as one, the pair
 * `matching` (#3D4EAD) and `social-awareness` (#8B4BD8) separate by ΔE 14 for
 * normal vision and 7.9 under protanopia, and `numeracy` (#F5B301) sits at
 * 1.85:1 against white. A pie chart or a stacked segment bar built from them
 * would be a chart where two slices are indistinguishable and one is invisible.
 *
 * ⚠️ SO THE SKILL CHART IS A LABELLED ROW PER SKILL — a swatch, the name, the
 * count and the bar — where the colour is a reminder of an identity the label
 * already gave. The tokens stay exactly as §3 fixes them (*"a second palette
 * invented for the sidebar is how two screens end up calling the same thing by
 * two colours"* — `nav.tsx` says the same thing), and nothing on screen depends
 * on telling two of them apart.
 *
 * ⚠️ AND THE DAY-BY-DAY CHART IS ONE SERIES IN ONE HUE, so it needs no legend at
 * all — the heading names it. A second series on the same axis is the mistake
 * this file will not make: minutes and activities have different units, and two
 * scales on one frame is a chart that can be read to say anything.
 */

/* ────────────────────────────────────────────────────────── a metric card ── */

/**
 * One number, with the division that produced it.
 *
 * 🔴 IT PRINTS THE FRACTION, NOT THE PERCENTAGE ALONE. OD-39 and contract D4:
 * *"81% of what?"* is the first question a school asks, and a parent who cannot
 * check a number about their child should not be given one. `StatCard` already
 * refuses a percentage as its `value`; this is that rule applied to the one
 * place a ratio genuinely belongs, and the fraction is the larger of the two.
 */
export function MetricCard({
  label,
  value,
  detail,
  tint,
  icon,
}: {
  label: ReactNode
  /** The headline. A count, or a fraction — never a bare percentage. */
  value: ReactNode
  /** The arithmetic, in words. This is the part that makes the headline honest. */
  detail?: ReactNode
  tint: string
  icon: ReactNode
}) {
  return (
    <div className={`${PANEL} play-card flex min-w-0 flex-col p-4`}>
      <span className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{
            background: `color-mix(in srgb, ${tint} 16%, transparent)`,
            color: tint,
          }}
        >
          {icon}
        </span>
        <span className="min-w-0 font-dash text-dash-xs font-bold text-muted">{label}</span>
      </span>
      {/* 🔴 `ink` at every tone, tabular. §4, and `sunshine` fails contrast as
          text outright (§2) — a coloured numeral would be colour carrying
          meaning alone the moment two cards sit side by side. */}
      <span className="tabular mt-2.5 text-dash-xl leading-none font-extrabold text-ink">
        {value}
      </span>
      {detail ? <span className="mt-1.5 font-dash text-dash-xs text-muted">{detail}</span> : null}
    </div>
  )
}

/* ─────────────────────────────────────────────────────── the accuracy ring ── */

/**
 * One ratio, as an arc — and the two numbers beside it.
 *
 * ⚠️ A RING RATHER THAN A GAUGE, AND THERE IS NO NEEDLE AND NO COLOURED ZONE. A
 * gauge implies a target ("you are in the red"), and there is no target here:
 * `activityModel.ts` refuses to invent one. The arc is this child's own answers
 * against their own answers, and the sentence under it is the division.
 */
export function AccuracyRing({
  ratio,
  label,
  caption,
  tint = 'var(--color-leaf)',
}: {
  ratio: Ratio
  label: ReactNode
  caption: ReactNode
  tint?: string
}) {
  /* r = 15.9155 makes the circumference exactly 100, so the dash array is the
     percentage with no arithmetic in the markup. */
  const offset = 100 - Math.max(0, Math.min(100, ratio.percent))

  return (
    <div className={`${PANEL} play-card flex min-w-0 items-center gap-4 p-4`}>
      <svg
        viewBox="0 0 36 36"
        className="h-16 w-16 shrink-0 -rotate-90"
        role="img"
        aria-label={typeof caption === 'string' ? caption : undefined}
      >
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          stroke="var(--color-line)"
          strokeWidth="3.5"
        />
        <circle
          className="play-ring"
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          stroke={tint}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray="100"
          strokeDashoffset={offset}
          style={{ '--ring-offset': offset } as CSSProperties}
        />
      </svg>
      <span className="flex min-w-0 flex-col">
        <span className="font-dash text-dash-xs font-bold text-muted">{label}</span>
        {/* 🔴 THE FRACTION IS THE HEADLINE AND THE PERCENTAGE IS THE FOOTNOTE.
            The other way round is the shape OD-39 refused. */}
        <span className="tabular mt-1 text-dash-lg leading-tight font-extrabold text-ink">
          {ratio.value}
          <span className="text-muted"> / </span>
          {ratio.of}
        </span>
        <span className="tabular mt-0.5 font-dash text-dash-xs text-muted">{ratio.percent}%</span>
        <span className="mt-1 font-dash text-dash-xs text-muted">{caption}</span>
      </span>
    </div>
  )
}

/* ───────────────────────────────────────────────────────── day-by-day bars ── */

export interface DayBarsProps {
  series: BarSeries
  /**
   * One line per bar, for the tooltip and the accessible name.
   *
   * ⚠️ A FUNCTION RATHER THAN A `lang`, WHICH IS WHY THIS COMPONENT TAKES NO
   * LANGUAGE AT ALL. A bar's label is a formatted date and a localised count —
   * two things the caller already has in hand — and a chart that took a `Lang`
   * would have to reach into `PARENT_COPY` and grow a second opinion about how a
   * date is written on the one screen that also writes dates elsewhere.
   */
  describe: (day: string, value: number) => string
  /** The tallest bar's value, spelled out — heights mean nothing without it. */
  maxLabel: ReactNode
  /** Optional: a tap opens the day. Absent on the report sheet, which cannot be tapped. */
  onPick?: (day: string) => void
  selected?: string | null
}

/**
 * One bar per day.
 *
 * 🔴 EVERY DAY IN THE WINDOW HAS A BAR, INCLUDING THE ZEROES. The server sends
 * empty days for exactly this reason: a gap where a zero should be reads as
 * missing data, and a chart that silently omitted quiet days would make every
 * child look like they play every day.
 *
 * ⚠️ THE BARS ARE `<div>`s IN A FLEX ROW, NOT AN `<svg>`. Twenty-eight rounded
 * rectangles with tooltips and tap targets is what CSS is for; an SVG would need
 * its own hit areas, its own focus ring and its own text sizing, and would end
 * up worse at all three. The ring above is an SVG because an arc is not a box.
 */
export function DayBars({ series, describe, maxLabel, onPick, selected = null }: DayBarsProps) {
  return (
    <div>
      <div className="flex h-28 items-end gap-[2px]" role="list">
        {series.bars.map((bar) => {
          const height = bar.value === 0 ? 0 : Math.max(6, Math.round(bar.ratio * 100))
          const title = describe(bar.day, bar.value)
          const isSelected = selected === bar.day

          const column = (
            <>
              {/*
                ⚠️ THE TRACK IS ALWAYS DRAWN AND THE FILL SITS INSIDE IT. Without
                a track, an empty day is an invisible column and the row loses its
                rhythm — a parent counting back "Monday, Tuesday…" along the axis
                needs every day to occupy space whether or not it has a value.
              */}
              <span
                aria-hidden="true"
                className="flex w-full flex-1 items-end rounded-sm"
                style={{
                  background: 'color-mix(in srgb, var(--color-line) 45%, transparent)',
                }}
              >
                <span
                  className="play-fill block w-full rounded-sm"
                  style={{
                    height: `${String(height)}%`,
                    /* One hue: this is a single series, so colour carries no
                       identity and needs none. */
                    background: isSelected ? 'var(--color-primary)' : 'var(--color-sky)',
                  }}
                />
              </span>
            </>
          )

          return (
            <div key={bar.day} role="listitem" className="flex min-w-0 flex-1 flex-col">
              {onPick ? (
                <button
                  type="button"
                  title={title}
                  aria-label={title}
                  aria-pressed={isSelected}
                  onClick={() => {
                    onPick(bar.day)
                  }}
                  className="flex h-28 w-full cursor-pointer flex-col justify-end rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                >
                  {column}
                </button>
              ) : (
                <span
                  title={title}
                  aria-label={title}
                  className="flex h-28 w-full flex-col justify-end"
                >
                  {column}
                </span>
              )}
            </div>
          )
        })}
      </div>
      <p className="tabular mt-2 font-dash text-dash-xs text-muted">{maxLabel}</p>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── the weekday rows ── */

/**
 * Which days of the week this child plays on.
 *
 * 🔴 IT DRAWS "2 OF 4", NEVER "50%". A window does not hold the same number of
 * each weekday — 28 days holds four of each, 30 holds five Mondays — so a raw
 * total would tell a parent their child plays most on Mondays because the window
 * contained more of them. The denominator is on every row.
 */
export function WeekdayBars({
  rows,
  weekdayLabels,
  format,
}: {
  rows: readonly WeekdayRow[]
  /** Sunday first, matching `Date.getUTCDay`. Localised by the caller. */
  weekdayLabels: readonly string[]
  format: (played: number, of: number) => string
}) {
  return (
    <ul className="flex flex-col gap-1.5">
      {rows.map((row) => {
        const ratio = row.days === 0 ? 0 : row.activeDays / row.days
        return (
          <li key={row.weekday} className="flex items-center gap-3">
            <span className="w-10 shrink-0 font-dash text-dash-xs font-bold text-muted">
              {weekdayLabels[row.weekday]}
            </span>
            <span
              aria-hidden="true"
              className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-pill"
              style={{
                background: 'color-mix(in srgb, var(--color-line) 45%, transparent)',
              }}
            >
              <span
                className="play-fill block h-full rounded-pill"
                style={{
                  width: `${String(Math.round(ratio * 100))}%`,
                  background: 'var(--color-grape)',
                }}
              />
            </span>
            <span className="tabular w-14 shrink-0 text-right font-dash text-dash-xs text-ink-soft">
              {format(row.activeDays, row.days)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/* ────────────────────────────────────────────────────────── the skill rows ── */

/**
 * The colour §3 fixes for a skill, content or motor.
 *
 * ⚠️ READ FROM THE TWO EXISTING MAPS RATHER THAN A THIRD. `GAME_FOR_SKILL` holds
 * the content skills' colours and `MOTOR_TINT` the motor ones — a table here
 * would be the *"second palette invented for one screen"* that makes two screens
 * call one skill by two colours.
 */
export function skillTint(skill: Skill): string {
  return isContentSkill(skill) ? GAME_FOR_SKILL[skill].tint : MOTOR_TINT[skill]
}

/**
 * What the window's play was spent on.
 *
 * 🔴 A LABELLED ROW PER SKILL, NOT A PIE AND NOT A STACKED BAR — see this file's
 * header. Two of §3's eight fixed colours are indistinguishable under protanopia
 * and one is invisible against white, so any form where the colour IS the label
 * would be unreadable for some parents and merely pretty for the rest.
 */
export function SkillBars({
  shares,
  name,
  format,
  total,
}: {
  shares: readonly SkillShare[]
  /** The skill's own name, localised. */
  name: (skill: Skill) => string
  format: (n: number, of: number) => string
  total: number
}) {
  return (
    <ul className="flex flex-col gap-3">
      {shares.map((row) => {
        const tint = skillTint(row.skill)
        return (
          <li key={row.skill} className="flex flex-col gap-1.5">
            <span className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 font-dash text-dash-sm font-bold text-ink">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: tint }}
                />
                {name(row.skill)}
              </span>
              <span className="tabular shrink-0 font-dash text-dash-xs text-muted">
                {format(row.events, total)}
              </span>
            </span>
            <span
              aria-hidden="true"
              className="h-2.5 overflow-hidden rounded-pill"
              style={{
                background: 'color-mix(in srgb, var(--color-line) 45%, transparent)',
              }}
            >
              <span
                className="play-fill block h-full rounded-pill"
                style={{
                  width: `${String(Math.round(row.share * 100))}%`,
                  background: tint,
                }}
              />
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** The plate for a game id, or null for one this build does not know. */
export const gameArtFor = (
  game: string,
): (typeof GAME_ART_BY_ID)[keyof typeof GAME_ART_BY_ID] | null =>
  (GAME_ART_BY_ID as Record<string, (typeof GAME_ART_BY_ID)[keyof typeof GAME_ART_BY_ID]>)[game] ??
  null
